import React, { useCallback, useEffect, useRef } from 'react';
import { klona } from 'klona';
import { nanoid } from '@reduxjs/toolkit';
import { closestCenter, DndContext, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useGetState } from 'ahooks';
import { Dialog } from 'antd-mobile';
import { padArrayToLength } from '@/utils';
import DragItem from './drag-item';

import cx from './index.module.scss';

interface Props {
  className?: string;
  points: Palpation.PointTypeWithDirection[];
  onChange: (points: Palpation.PointType[]) => void;
  onDeleteNodule: (noduleId: string, cb: VoidFunc) => void;
}

const DragContent: React.FC<Props> = ({ className, points, onChange, onDeleteNodule }) => {
  const [list, setList, getList] = useGetState([]);
  const hasOpenDialog = useRef(false);

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 5,
        delay: 200, // 设置点击时间为 200 毫秒
        tolerance: 5, // 设置移动容差为 5 像素
      },
    })
  );

  // 删除痛点
  const handleDelTubercle = useCallback(
    index => () => {
      if (hasOpenDialog.current) return;
      hasOpenDialog.current = true;
      Dialog.confirm({
        title: '确定删除该痛点吗？',
        content: '该操作将无法撤销',
        confirmText: '确认',
        async onConfirm() {
          return new Promise(async (resolve, reject) => {
            try {
              await onDeleteNodule?.(getList()[index]?.noduleId, () => {
                const newList = klona(getList());
                newList.splice(index, 1);
                setList(newList);
                onChange(newList);
                resolve();
              });
            } catch (e) {
              reject(e);
            }
          });
        },
        onClose() {
          hasOpenDialog.current = false;
        },
      });
    },
    [getList, onChange, onDeleteNodule, setList]
  );

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;

      if (active.id !== over.id) {
        const oldIndex = list.findIndex(item => item?.noduleId === active?.id);
        const newIndex = list.findIndex(item => item?.noduleId === over?.id);

        const newList = arrayMove(list, oldIndex, newIndex);
        setList(newList);
        onChange(newList);
      }
    },
    [list, onChange, setList]
  );

  useEffect(() => {
    setList(points?.map(item => ({ ...item, id: item.noduleId })) || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  return (
    <div className={cx(className, 'drag-wrapper')}>
      <DndContext
        autoScroll={{ threshold: { x: 0, y: 0.2 } }}
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={list} strategy={verticalListSortingStrategy}>
          {padArrayToLength(list, 8).map((item, index) =>
            item?.noduleId ? (
              <DragItem
                handleDelTubercle={handleDelTubercle(index)}
                key={item.id || item.noduleId}
                id={item.noduleId}
                item={item}
              />
            ) : (
              <div key={nanoid()} className={cx('empty-fill-div')}>
                痛点
              </div>
            )
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default DragContent;
